import { ChangeEvent } from "react";
import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ErrorMessageComponent } from "@/Common/Components/Errors/ErrorMessageComponent";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { SubscriptionPlanDto } from "@/Common/Models/Portal/ClinicConfig/SubscriptionPlanDto";
import { ChangeSubscriptionResponse, GetSubscriptionPlansResponse, SubscriptionGroupMyleDto, SubscriptionPaymentDto } from "@/Common/Models/Portal/MedfarPortal";
import { PaymentInterval } from "@/Common/Models/Portal/MedfarPortalEnums";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";

enum SubscriptionChangeAction {
    "Change" = 1,
    "Reset" = 2,
    "Cancel" = 3
}

interface ISubscriptionChangePlanPopup {
    subscription: SubscriptionGroupMyleDto;
    currentPlanId: string;
    patientContactId: string;
    onCloseCallBack: () => void;
    changeAction: SubscriptionChangeAction;
}

class SubscriptionChangePlanState {
    constructor(s: SubscriptionChangePlanState = null) {
        if (s == null)
            return;

        this.isLoading = s.isLoading;
        this.errors = s.errors;
        this.isModifyPlanPopupVisible = s.isModifyPlanPopupVisible;
        this.isConfirmAccountSuspensionPopupVisible = s.isConfirmAccountSuspensionPopupVisible;
        this.currentPlan = s.currentPlan;
        this.plans = s.plans;
        this.selectedPlanId = s.selectedPlanId;
        this.currentInterval = s.currentInterval;
        this.selectedIntervalId = s.selectedIntervalId;
        this.effectiveDate = s.effectiveDate;
        this.monthlyRate = s.monthlyRate;
        this.annualRate = s.annualRate;
        this.changeSubscriptionPreview = s.changeSubscriptionPreview;
    }

    public isLoading: boolean;
    public errors: string[];
    public isModifyPlanPopupVisible: boolean;
    public isConfirmAccountSuspensionPopupVisible: boolean;

    public currentPlan: SubscriptionPlanDto;
    public plans: GetSubscriptionPlansResponse;
    public selectedPlanId: string;

    public currentInterval: number;
    public selectedIntervalId: number;

    public effectiveDate = moment().toDate();
    public monthlyRate: number;
    public annualRate: number;

    public changeSubscriptionPreview: ChangeSubscriptionResponse;
}

class SubscriptionChangePlan extends BaseReactComponent<ISubscriptionChangePlanPopup, SubscriptionChangePlanState> {
    private componentId = "portal_trx_changePlan";

    constructor(props: ISubscriptionChangePlanPopup) {
        super(props);
        const initialState = new SubscriptionChangePlanState();
        initialState.selectedIntervalId = this.props.subscription.CurrentGroupPlan.PaymentInterval;
        this.state = initialState;
    }

    public componentDidMount(): void {
        this.getPlans();
        overlayStore.showOverlay("SubscriptionChangePlan");

        const element = $("#" + this.componentId);
        const dragOptions = {
            containment: document.body,
            handle: ".dragHandle",
            stop() {
                element.draggable("destroy");
                element.css("height", "auto");
                window.setTimeout(
                    () => element.draggable(dragOptions),
                    50
                );
            }
        };
        element.draggable(dragOptions);
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        overlayStore.hideOverlay("SubscriptionChangePlan");
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        const self = this;

        let hasSelectionChanged = false;
        if (this.state.changeSubscriptionPreview) {
            hasSelectionChanged =
                this.props.changeAction === SubscriptionChangeAction.Cancel ||
                this.props.changeAction === SubscriptionChangeAction.Reset ||
                (
                    this.state.changeSubscriptionPreview.NewGroupPlan && (!this.state.changeSubscriptionPreview.EffectiveGroupPlan ||
                        this.state.changeSubscriptionPreview.EffectiveGroupPlan.PaymentInterval !== this.state.changeSubscriptionPreview.NewGroupPlan.PaymentInterval ||
                        this.state.changeSubscriptionPreview.EffectiveGroupPlan.Rate.Total !== this.state.changeSubscriptionPreview.NewGroupPlan.Rate.Total ||
                        this.state.changeSubscriptionPreview.ChangePayments.length > 0
                    )
                );
        }

        let saveDisabled = this.state.isLoading || !hasSelectionChanged;
        let estimateIntervalLabel = "N/A";
        let newEstimatePlan: SubscriptionPlanDto = null;
        let changePayments: JSX.Element = null;

        if (this.state.changeSubscriptionPreview) {
            if (!this.state.changeSubscriptionPreview.NewGroupPlan)
                estimateIntervalLabel = "";
            else if (this.state.changeSubscriptionPreview.NewGroupPlan.PaymentInterval === PaymentInterval.Annually)
                estimateIntervalLabel = cacheStore.GetString("YearlyMasculineLabel");
            else if (this.state.changeSubscriptionPreview.NewGroupPlan.PaymentInterval === PaymentInterval.Monthly)
                estimateIntervalLabel = cacheStore.GetString("MonthlyMasculineLabel");
            else if (this.state.changeSubscriptionPreview.NewGroupPlan.PaymentInterval === PaymentInterval.Once)
                estimateIntervalLabel = cacheStore.GetString("LifetimeLabel");

            newEstimatePlan = this.state.changeSubscriptionPreview.NewGroupPlan
                ? cacheStore.GetPortalSubscriptionPlanInfo(this.state.changeSubscriptionPreview.NewGroupPlan.SubscriptionPlanId)
                : null;

            if (this.state.changeSubscriptionPreview.ChangePayments && this.state.changeSubscriptionPreview.ChangePayments.length) {
                const changePaymentItems = this.state.changeSubscriptionPreview.ChangePayments.map((changePayment: SubscriptionPaymentDto, i: number) => (
                    <tr key={"itemdetail_" + i}>
                        <td>{changePayment.InternalDescription}</td>
                        <td>{changePayment.Amount.Subtotal.toFixed(2)}</td>
                        <td>{changePayment.Amount.Tax.toFixed(2)}</td>
                        <td>{changePayment.Amount.Total.toFixed(2)}</td>
                    </tr>
                ));

                changePayments = (
                    <div>
                        <div>
                            <h4>{cacheStore.GetString("TransactionChangesLabel")}:</h4>
                        </div>
                        <div className="scroll-form">
                            <div className="flex-container p-crf">
                                <div className="c1">
                                    <span>{cacheStore.GetString("DetailsLabel")}</span>
                                </div>
                                <div className="c2">
                                    <table className="trx-item-details">
                                        <thead>
                                            <tr>
                                                <td>{cacheStore.GetString("DescriptionLabel")}</td>
                                                <td>{cacheStore.GetString("UnitPriceLabel")}</td>
                                                <td>{cacheStore.GetString("ApplicableTaxesLabel")}</td>
                                                <td>{cacheStore.GetString("PriceLabel")}</td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {changePaymentItems}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
        }

        const actionText = cacheStore.GetString(SubscriptionChangeAction[this.props.changeAction] + "SubscriptionPlanLabel");
        const buttonClass = this.props.changeAction === SubscriptionChangeAction.Cancel ? "btn btn-warning" : "btn btn-primary";

        const header = (
            <div className="portal-messages-header">
                <div className="itemGrow dragHandle">
                    <h3 className="thin-font">{actionText}</h3>
                </div>
                <div>
                    <a className="close" onClick={this.onClosePopup.bind(this)} aria-label="close" style={{ paddingTop: "10px" }}>×</a>
                </div>
                <h4 />
            </div>
        );

        const footer = (
            <div className="p-footer">
                <div className="col-lg-12 text-right">
                    <button className="btn btn-default push-right-5" onClick={this.onClosePopup.bind(this)}>
                        {cacheStore.GetString("CancelLabel")}<span />
                    </button>
                    <button className={buttonClass} disabled={saveDisabled} onClick={self.onChangePlan.bind(self)}>
                        {actionText}<span />
                    </button>
                </div>
            </div>
        );

        if (this.state.errors && this.state.errors.length > 0)
            return (
                <div className="popup portal-popup" id={this.componentId}>
                    {header}
                    <div className="p-body">
                        <ErrorMessageComponent errors={this.state.errors} />
                    </div>
                    {footer}
                </div>
            );

        if (this.state.isLoading)
            return (
                <div className="popup portal-popup" id={this.componentId}>
                    {header}
                    <div className="p-body">
                        <InformationLoadingComponent smallVersion={true} customMessage={cacheStore.GetString("WaitForPortalLabel")} />
                    </div>
                    {footer}
                </div>
            );

        const intervals: KeyValuePair<PaymentInterval, string>[] = [];

        if (this.state.currentPlan) {
            if (this.state.currentPlan.MonthlyRate)
                intervals.push(new KeyValuePair(PaymentInterval.Monthly, cacheStore.GetString("MonthlyMasculineLabel")));

            if (this.state.currentPlan.AnnualRate)
                intervals.push(new KeyValuePair(PaymentInterval.Annually, cacheStore.GetString("YearlyMasculineLabel")));

            if (this.state.currentPlan.LifetimeRate)
                intervals.push(new KeyValuePair(PaymentInterval.Once, cacheStore.GetString("LifetimeLabel")));
        }

        return (
            <div className="popup portal-popup" id={this.componentId}>
                {header}
                <div className="p-body">
                    <div>
                        <div className="scroll-form">
                            {this.state.currentPlan && (
                                <div>
                                    <div className="flex-container p-crf">
                                        <div className="c1">
                                            <span>{cacheStore.GetString("NameLabel")}</span>
                                        </div>
                                        <div className="c2">
                                            <div className="selector">
                                                {this.state.plans != null && (
                                                    <select disabled={this.state.isLoading || this.props.changeAction === SubscriptionChangeAction.Cancel} className="form-control" value={this.state.selectedPlanId} onChange={this.onChangePlanSelection.bind(this)}>
                                                        {this.state.plans.SubscriptionPlans.map((p, i) => {
                                                            const displayName = p.SubscriptionPlanId === self.props.currentPlanId
                                                                ? p.Name + " *"
                                                                : p.Name;

                                                            return <option value={p.SubscriptionPlanId} key={"subscriptionPlan" + i}>{displayName}</option>;
                                                        })}
                                                    </select>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-container p-crf">
                                        <div className="c1">
                                            <span>{cacheStore.GetString("IntervalLabel")}</span>
                                        </div>
                                        <div className="c2">
                                            <div className="selector">
                                                {!!intervals && (
                                                    <select disabled={this.state.isLoading || this.props.changeAction === SubscriptionChangeAction.Cancel} className="form-control" value={this.state.selectedIntervalId.toString()} onChange={this.onSelectedIntervalChange.bind(this)}>
                                                        {intervals.map(interval => <option key={`int_${interval.Key}`} value={interval.Key.toString()}>{interval.Value}</option>)}
                                                    </select>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-container p-crf">
                                        <div className="c1">
                                            <span>{cacheStore.GetString("EffectiveDateLabel")}</span>
                                        </div>
                                        <div className="c2">
                                            <DateSelector
                                                disabled={this.state.isLoading}
                                                date={this.state.effectiveDate}
                                                dateFormat="yyyy-MM-dd"
                                                id="subsplan_dp"
                                                onChangeCallback={this.onEffectiveDateChange.bind(this)}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-container p-crf">
                                        <div className="c1">
                                            <span>{cacheStore.GetString("DescriptionLabel")}</span>
                                        </div>
                                        <div className="c2">
                                            <strong>
                                                <span>{this.state.currentPlan.Description}</span>
                                            </strong>
                                        </div>
                                    </div>
                                    {!!this.state.currentPlan.MonthlyRate && (
                                        <div className="flex-container p-crf">
                                            <div className="c1">
                                                <span>{cacheStore.GetString("MonthlyRateLabel")}</span>
                                            </div>
                                            <div className="c2">
                                                <strong>
                                                    <span>{this.state.currentPlan.MonthlyRate}$</span>
                                                </strong>
                                            </div>
                                        </div>
                                    )}
                                    {!!this.state.currentPlan.AnnualRate && (
                                        <div className="flex-container p-crf">
                                            <div className="c1">
                                                <span>{cacheStore.GetString("YearlyRateLabel")}</span>
                                            </div>
                                            <div className="c2">
                                                <strong>
                                                    <span>{this.state.currentPlan.AnnualRate}$</span>
                                                </strong>
                                            </div>
                                        </div>
                                    )}
                                    {!!this.state.currentPlan.LifetimeRate && (
                                        <div className="flex-container p-crf">
                                            <div className="c1">
                                                <span>{cacheStore.GetString("LifetimeRateLabel")}</span>
                                            </div>
                                            <div className="c2">
                                                <strong>
                                                    <span>{this.state.currentPlan.LifetimeRate}$</span>
                                                </strong>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {this.state.currentPlan &&
                                this.state.changeSubscriptionPreview &&
                                    <hr />
                            }
                            {this.state.changeSubscriptionPreview && (
                                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                    <div>
                                        <div>
                                            <h4>{cacheStore.GetString("PlanEstimateLabel")} </h4>
                                        </div>
                                        {this.state.changeSubscriptionPreview.NewGroupPlan && (
                                            <div className="scroll-form">
                                                <div className="flex-container p-crf">
                                                    <div className="c1">
                                                        <span>{cacheStore.GetString("SubscriptionLabel")}</span>
                                                    </div>
                                                    <div className="c2">
                                                        <strong>{newEstimatePlan.InternalName}</strong>
                                                    </div>
                                                </div>
                                                <div className="flex-container p-crf">
                                                    <div className="c1">
                                                        <span>{cacheStore.GetString("PaymentIntervalLabel")}</span>
                                                    </div>
                                                    <div className="c2">
                                                        <strong>{estimateIntervalLabel}</strong>
                                                    </div>
                                                </div>
                                                <div className="flex-container p-crf">
                                                    <div className="c1">
                                                        <span>{cacheStore.GetString("NewRateLabel")}</span>
                                                    </div>
                                                    <div className="c2">
                                                        <strong>{this.state.changeSubscriptionPreview.NewGroupPlan.Rate.Total.toFixed(2)}</strong>
                                                    </div>
                                                </div>
                                                <div className="flex-container p-crf">
                                                    <div className="c1">
                                                        <span>{cacheStore.GetString("NextPaymentDateLabel")}</span>
                                                    </div>
                                                    <div className="c2">
                                                        <strong>{moment(this.state.changeSubscriptionPreview.NewGroupPlan.FirstBillDate).format("YYYY-MM-DD")}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {changePayments}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {footer}
            </div>
        );
    }

    private getPlans(): void {
        const c = new Client();
        c.Post(apiSettings.buildApiPath(), ServerOperations.Server.Portal_Proxy_GetPlans, {}, response => this.onGetPlanSuccess(response), error => this.onError(error));

        const s = new SubscriptionChangePlanState();
        s.isLoading = true;
        this.setState(s);
    }

    private onGetPlanSuccess(response: GetPlansResponse): void {
        const self = this;

        const errors = response.Errors as ErrorMessage[];
        const changePlanState = new SubscriptionChangePlanState(this.state);
        if (errors && errors.length) {
            const errorMessages = errors.map((e: ErrorMessage) => e.Message);

            changePlanState.isLoading = false;
            changePlanState.errors = errorMessages;
            self.setState(changePlanState);

            return;
        }
        if (response.Plans == null)
            return;

        changePlanState.plans = response.Plans;

        let loadPlan = false;
        if (self.state.selectedPlanId == null && self.props.currentPlanId != null) {
            changePlanState.selectedPlanId = self.props.currentPlanId;
            loadPlan = true;

            const plansFilter = changePlanState.plans.SubscriptionPlans.filter(x => x.SubscriptionPlanId === self.props.currentPlanId);
            changePlanState.currentPlan = plansFilter != null && plansFilter.length > 0 ? plansFilter[0] : null;
            changePlanState.isLoading = false;
        }

        if (loadPlan) {
            self.setState(changePlanState, () => self.onLoadPlanEstimate());
        }
    }

    private onChangePlanSelection(e: ChangeEvent<HTMLSelectElement>): void {
        const self = this;
        const p = e.target.value;

        self.mutateState(s => {
            s.selectedPlanId = p;

            if (p !== "") {
                s.currentPlan =
                    self.state.plans.SubscriptionPlans.filter(pr => pr.SubscriptionPlanId === p)[0];
            }
            else {
                s.currentPlan = null;
            }
            return s;
        }, () => self.onLoadPlanEstimate());
    }

    private onLoadPlanEstimate(): void {
        const t = new Client();

        let requestData: any;
        if (this.props.changeAction === SubscriptionChangeAction.Cancel) {
            requestData = {
                Action: SubscriptionChangeAction[this.props.changeAction],
                SubscriptionId: this.props.subscription.SubscriptionGroupId,
                EffectiveDate: this.state.effectiveDate
            };
        }
        else {
            requestData = {
                Action: SubscriptionChangeAction[this.props.changeAction],
                SubscriptionId: this.props.subscription.SubscriptionGroupId,
                SubscriptionPlanId: this.state.selectedPlanId,
                EffectiveDate: this.state.effectiveDate,
                PaymentInterval: this.state.selectedIntervalId
            };
        }

        const self = this;
        t.Post(apiSettings.buildApiPath(), ServerOperations.Server.Portal_Proxy_PreviewChangeSubscription, requestData, response => self.onLoadPlanEstimateSuccess(response), err => self.onError(err));

        const s = new SubscriptionChangePlanState(this.state);
        s.isLoading = true;
        this.setState(s);
    }

    private onLoadPlanEstimateSuccess(response: GetPlansPreviewResponse): void {
        const self = this;
        const errors = response.Errors as ErrorMessage[];
        const s = new SubscriptionChangePlanState(self.state);

        if (errors && errors.length) {
            s.errors = errors.map((e: ErrorMessage) => e.Message);
        }
        else {
            s.changeSubscriptionPreview = response.SubscriptionChangeResult;
        }
        s.isLoading = false;
        self.setState(s);
    }

    private onClosePopup(): void {
        const self = this;
        if (self.props.onCloseCallBack != null)
            self.props.onCloseCallBack();
    }

    private onEffectiveDateChange(date: Date): void {
        const s = new SubscriptionChangePlanState(this.state);
        s.effectiveDate = date;
        this.setState(s, () => { this.onLoadPlanEstimate(); });
    }

    private onSelectedIntervalChange(e): void {
        const val = e.target.value;
        const self = this;

        const s = new SubscriptionChangePlanState(this.state);
        s.selectedIntervalId = val;
        this.setState(s, () => self.onLoadPlanEstimate());
    }

    private onChangePlan(): void {
        const t = new Client();

        let requestData: any;
        if (this.props.changeAction === SubscriptionChangeAction.Cancel) {
            requestData = {
                Action: SubscriptionChangeAction[this.props.changeAction],
                SubscriptionId: this.props.subscription.SubscriptionGroupId,
                EffectiveDate: this.state.effectiveDate
            };
        }
        else {
            requestData = {
                Action: SubscriptionChangeAction[this.props.changeAction],
                SubscriptionId: this.props.subscription.SubscriptionGroupId,
                SubscriptionPlanId: this.state.selectedPlanId,
                EffectiveDate: this.state.effectiveDate,
                PaymentInterval: this.state.selectedIntervalId
            };
        }

        const self = this;
        t.Post(apiSettings.buildApiPath(), ServerOperations.Server.Portal_Proxy_ChangeSubscription, requestData, response => self.onChangePlanSuccess(response), err => self.onError(err));

        const s = new SubscriptionChangePlanState(this.state);
        s.isLoading = true;
        this.setState(s);
    }

    private onChangePlanSuccess(response: ServerEvent): void {
        const errors = response.Errors as ErrorMessage[];

        if (errors.length === 0) {
            const op = new LocalEvent(LocalOperations.Portal.Subscription_Load);
            op.AddItem("patientContactId", this.props.patientContactId);
            op.AddItem("subscriptionId", this.props.subscription.SubscriptionGroupId);
            LocalEventDispatcher.dispatch(op);
            this.onClosePopup();
        }
        else {
            const errorMessages = errors.map((e: ErrorMessage) => e.Message);

            const s = new SubscriptionChangePlanState(this.state);
            s.isLoading = false;
            s.errors = errorMessages;
            this.setState(s);
        }
    }

    private onError(error: string): void {
        const self = this;
        const s = new SubscriptionChangePlanState(self.state);
        s.isLoading = false;
        s.errors = [error];
        self.setState(s);
    }
}

class GetPlansResponse extends ServerEvent {
    public Plans: GetSubscriptionPlansResponse;
}

class GetPlansPreviewResponse extends ServerEvent {
    public SubscriptionChangeResult: ChangeSubscriptionResponse;
}

export { SubscriptionChangeAction, SubscriptionChangePlan };
